import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { defaultProfileName, initialProfileSettings } from "../../Config/config";
import RoomListPanels from "./RoomListPanels";
import { GameContext } from "../../Context/GameContext";
import useEscapeRoomApi from "../../Hooks/useEscapeRoomApi";
import { ProfileSettings, RoomStates, RoomStatus } from "../../Types/Game";

/**
 * Settings
 * --------
 * Admin settings editor for creating/attaching a profile to an Event, configuring time/branding,
 * toggles (school names, single-room mode), and selecting/reordering rooms.
 * Behaviour:
 * - Load profiles, optionally pre-load the attached profile for a given `eventName`.
 * - Create a new profile or select an existing one; update rooms (active + order).
 * - Save settings to backend, attach the profile to the event, and optionally close/navigate home.
 */

// TYPES ------------------------------------------------------------
interface SettingsProps {
  eventName?: string;
  admin: boolean;
  onClose?: () => void;
}
type BrandingKey = "gemba" | "actica" | "ntegra";

// COMPONENT --------------------------------------------------------
const Settings = ({ eventName, admin, onClose }: SettingsProps) => {
  // CONTEXT & NAV --------------------------------------------------
  const { hasLoadedRoomStates, roomStates, applyProfile } = useContext(GameContext);
  const navigate = useNavigate();

  // API ------------------------------------------------------------
  const { saveProfileSettings, getAllProfiles, getProfileSettings, attachProfile } =
    useEscapeRoomApi();

  // STATE ----------------------------------------------------------
  const [updatedRoomStates, setUpdatedRoomStates] = useState<RoomStates | undefined>();
  const [escapeRoomTimeMinutes, setEscapeRoomTimeMinutes] = useState<number>(20);

  const [branding, setBranding] = useState<BrandingKey>("gemba");

  const [profileName, setProfileName] = useState<string>("");
  const [profiles, setProfiles] = useState<ProfileSettings[] | undefined>(undefined);
  const [selectedProfile, setSelectedProfile] = useState<ProfileSettings | undefined>(undefined);
  const [attachedProfile, setAttachedProfile] = useState<ProfileSettings | undefined>(undefined);

  const [feedbackMessage, setFeedbackMessage] = useState<string | undefined>(undefined);
  const [isSaveDisabled, setIsSaveDisabled] = useState<boolean>(true);
  const [isCreateProfile, setIsCreateProfile] = useState<boolean>(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [newEventName, setNewEventName] = useState<string>("");

  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isSchoolInputActive, setIsSchoolInputActive] = useState<boolean>(false);
  const [singleRoom, setSingleRoom] = useState<boolean>(true);
  const [showDifficultyIndicators, setShowDifficultyIndicators] = useState<boolean>(true);

  // HELPERS --------------------------------------------------------
  /** Coerce API time (seconds or minutes) into a sane minutes value (min fallback = 20). */
  const coerceMinutesFromApi = (value: unknown): number => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return 20;

    // If it's clearly seconds, convert to minutes:
    // - exact multiples of 60 (e.g., 60, 900, 1200, …)
    // - or very large numbers (legacy seconds like 750) as a safety net
    if (n % 60 === 0 || n > 180) return Math.round(n / 60);

    // Otherwise assume minutes already
    return Math.round(n);
  };

  /** Loosely-typed room state as it may appear in legacy/unvalidated data. */
  type LegacyRoomState = {
    order?: unknown;
    active?: unknown;
    status?: RoomStatus;
  };

  /** Ensures rooms have numeric `order` and boolean `active`, with safe fallbacks. */
  const normalizeRooms = (rooms?: Record<string, LegacyRoomState>, fallback?: RoomStates): RoomStates => {
    // Seed from the app defaults so rooms added after a profile was saved
    // (e.g. room99, the Experiment Room) still appear in the room list.
    const source: Record<string, LegacyRoomState> = {
      ...initialProfileSettings.rooms,
      ...(rooms ?? fallback ?? {}),
    };
    const out: RoomStates = {};

    Object.entries(source).forEach(([key, val]) => {
      if (!val) return;
      const rawOrder = val.order;
      const numOrder = typeof rawOrder === "number" ? rawOrder : Number(rawOrder);

      const rawActive = val.active;
      const boolActive =
        typeof rawActive === "boolean"
          ? rawActive
          : String(rawActive).toLowerCase() === "true";

      out[key] = {
        ...val,
        order: Number.isFinite(numOrder) ? numOrder : 9999, // push to bottom if missing/invalid
        active: boolActive,
        status: val.status ?? RoomStatus.Open,
      };
    });

    return out;
  };

  // EFFECTS --------------------------------------------------------
  // Enable/disable Save based on changes
  useEffect(() => {
    const hasChanges = updatedRoomStates !== undefined || isDirty;
    setIsSaveDisabled(!hasChanges);
  }, [updatedRoomStates, isDirty]);

  // Initial load of profiles and the attached event profile (when provided)
  useEffect(() => {
    const loadProfiles = async () => {
      const result: ProfileSettings[] = await getAllProfiles();
      setProfiles(result);

      if (eventName) {
        const currentProfile = await getProfileSettings(eventName);
        if (currentProfile) {
          setAttachedProfile(currentProfile);
          setSelectedProfile(currentProfile);
          setUpdatedRoomStates(normalizeRooms(currentProfile.rooms, roomStates));
          setEscapeRoomTimeMinutes(coerceMinutesFromApi(currentProfile.escapeRoomTime));
          setIsSchoolInputActive(Boolean(currentProfile.schoolInput));
          setSingleRoom(Boolean(currentProfile.singleRoom));
          setShowDifficultyIndicators(currentProfile.showDifficultyIndicators !== false);
          setReadOnly(false);
          setNewEventName(eventName ?? "");
          setBranding((currentProfile.branding as BrandingKey) ?? "gemba");
        }
      }

      setIsProfileLoaded(true);
    };

    loadProfiles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // HANDLERS -------------------------------------------------------
  const handleSchoolInputChange = () => {
    setIsSchoolInputActive((prev) => !prev);
    setIsDirty(true);
  };

  const handleEscapeRoomTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const parsed = Number(e.target.value);
    setEscapeRoomTimeMinutes(Number.isNaN(parsed) ? 0 : parsed);
    setIsDirty(true);
  };

  /** Reset room-related state to a base/empty configuration for a new profile. */
  const clearRoomState = () => {
    setUpdatedRoomStates(undefined);
    setIsSchoolInputActive(false);
    setSingleRoom(true);
    setShowDifficultyIndicators(true);
    setIsDirty(false);
    setReadOnly(false);
  };

  /** Load an existing profile by name and map its values into local state for editing. */
  const handleProfileSelect = (name?: string) => {
    if (!name) return;
    const loaded = profiles?.find((p) => p.profileName === name);
    if (loaded) {
      setSelectedProfile(loaded);
      setUpdatedRoomStates(normalizeRooms(loaded.rooms, roomStates));
      setEscapeRoomTimeMinutes(coerceMinutesFromApi(loaded.escapeRoomTime));
      setIsSchoolInputActive(Boolean(loaded.schoolInput));
      setSingleRoom(Boolean(loaded.singleRoom));
      setShowDifficultyIndicators(loaded.showDifficultyIndicators !== false);
      setBranding(loaded.branding ?? "gemba");
      setReadOnly(false);
      setIsDirty(true);
    }
  };

  /** Apply a full room-selection/order update from the drag-and-drop panels. */
  const handleRoomsChange = (next: RoomStates) => {
    setUpdatedRoomStates(next);
    setIsDirty(true);
  };

  /** Persist settings, attach the chosen profile to the event, and refresh local state. */
  const saveSettings = async () => {
    try {
      const eventKey = (newEventName || eventName || "").trim();

      if (!eventKey) {
        setFeedbackMessage("Please enter an Event name before saving.");
        return;
      }

      // Normalise UI minutes -> integer seconds (min 60s)
      const minutes = Math.max(1, Math.round(escapeRoomTimeMinutes || 0));
      const seconds = minutes * 60;

      // Decide profile target (existing or new)
      const targetProfileName = isCreateProfile
        ? (profileName || defaultProfileName)
        : (selectedProfile?.profileName || defaultProfileName);

      const body = {
        profileName: targetProfileName,
        escapeRoomTime: seconds,
        rooms: normalizeRooms(updatedRoomStates ?? roomStates),
        schoolInput: isSchoolInputActive,
        singleRoom,
        showDifficultyIndicators,
        branding,
      } as ProfileSettings & { branding?: BrandingKey };

      // Save + attach, then read back what the game will consume
      await saveProfileSettings(body, eventKey);
      await attachProfile(eventKey, targetProfileName);
      const fresh = await getProfileSettings(eventKey);
      // console.log("fresh profile after save+attach", fresh);

      if (fresh) {
        setSelectedProfile(fresh);
        setEscapeRoomTimeMinutes(coerceMinutesFromApi(fresh.escapeRoomTime));
        setIsSchoolInputActive(Boolean(fresh.schoolInput));
        setSingleRoom(Boolean(fresh.singleRoom));
        setShowDifficultyIndicators(fresh.showDifficultyIndicators !== false);
        setUpdatedRoomStates(normalizeRooms(fresh.rooms, roomStates));
        setBranding(fresh.branding ?? "gemba");
      } else {
        // Fallback: reflect what we attempted to save
        setSelectedProfile((p) => (p ? { ...p, ...body } : body));
        setEscapeRoomTimeMinutes(minutes);
        applyProfile(fresh);
      }

      setIsDirty(false);
      setIsSaveDisabled(true);
      setFeedbackMessage("Saved settings and updated the event.");

      if (onClose) onClose();
      else navigate("/");
    } catch (err) {
      setFeedbackMessage("Error saving settings.");
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    else navigate("/");
  };

  // DERIVED / VALIDATION -------------------------------------------
  if (!isProfileLoaded) return null;

  // Renaming an event creates a new event:<name> pointer in the backend
  // without removing the old one (ERD-4) — lock the name once an event
  // already exists rather than allowing an in-place rename.
  const isExistingEvent = Boolean(eventName);

  const baseRooms: RoomStates = (updatedRoomStates ?? roomStates) || {};
  const activeRoomCount = Object.values(baseRooms).filter((r) => r && r.active).length;

  const hasEventName = Boolean((eventName ?? newEventName)?.trim());
  const hasProfile = isCreateProfile
    ? Boolean(profileName?.trim())
    : Boolean(selectedProfile?.profileName);
  const hasRooms = activeRoomCount > 0;

  const validationErrors: string[] = [];
  if (!hasEventName) validationErrors.push("Enter an Event name.");
  if (!hasProfile) validationErrors.push("Select an existing profile or enter a new profile name.");
  if (!hasRooms) validationErrors.push("Select at least one Escape Room.");

  const isFormValid = hasEventName && hasProfile && hasRooms;

  // RENDER ----------------------------------------------------------
  return (
    <div className="settings-container">
      <div className="settings-grid">
        <div className="settings-grid__left">
          {/* Event name ------------------------------------------------ */}
          <div className="form-group">
            <label className="label-text">Event Name</label>
            <input
              className={`settings-input ${isExistingEvent ? "readOnly" : ""}`}
              type="text"
              value={newEventName}
              placeholder="Enter a name for your event ..."
              readOnly={isExistingEvent}
              title={isExistingEvent ? "Event names can't be changed after creation" : undefined}
              onChange={(e) => {
                setNewEventName(e.target.value);
                setIsDirty(true);
              }}
            />
          </div>

          {/* Profile select / create ---------------------------------- */}
          {!isCreateProfile ? (
            <div className="form-group">
              <label className="label-text">Select Existing Profile</label>

              <div className="select-block">
                <select
                  className="settings-input"
                  id="profileSelect"
                  onChange={(e) => handleProfileSelect(e.target.value)}
                  value={selectedProfile?.profileName ?? ""}
                >
                  <option value="" disabled>
                    Existing profiles ...
                  </option>
                  {profiles &&
                    profiles.map((profile) => (
                      <option key={profile.profileName} value={profile.profileName} id="profileName">
                        {profile.profileName === attachedProfile?.profileName
                          ? `${profile.profileName} (current)`
                          : profile.profileName}
                      </option>
                    ))}
                </select>

                <div className="or-row">
                  <span className="or-text">
                    <strong>Or, why not</strong>
                  </span>
                  <button
                    className="btn-blue"
                    onClick={() => {
                      clearRoomState();
                      setIsCreateProfile(true);
                      setProfileName("");
                      setUpdatedRoomStates(initialProfileSettings.rooms);
                      setIsDirty(true);
                    }}
                  >
                    Create a new profile
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="form-group">
              <label className="label-text">New Profile Name</label>

              <div className="select-block">
                <input
                  className="settings-input"
                  type="text"
                  value={profileName}
                  placeholder="Enter a name for your new profile ..."
                  onChange={(e) => {
                    setProfileName(e.target.value);
                    setIsDirty(true);
                  }}
                />

                <div className="or-row">
                  <span className="or-text">
                    <strong>Or, why not</strong>
                  </span>
                  <button
                    className="btn-blue"
                    onClick={() => {
                      setIsCreateProfile(false);
                      setReadOnly(true);
                      if (selectedProfile?.profileName) {
                        handleProfileSelect(selectedProfile.profileName);
                      }
                    }}
                  >
                    Choose an existing profile
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Time + toggles ------------------------------------------- */}
          <div className="settings-stack">
            <div>
              <span className="label-text">Escape Room time (in minutes)</span>
              <input
                type="number"
                className={`settings-input ${readOnly ? "readOnly" : ""}`}
                name="Escape Room Time"
                value={escapeRoomTimeMinutes}
                onChange={handleEscapeRoomTimeChange}
                min="1"
                readOnly={readOnly}
              />
            </div>

            <div>
              <label className="custom-check checkbox-label">
                <input
                  type="checkbox"
                  name="School Input"
                  checked={isSchoolInputActive}
                  onChange={handleSchoolInputChange}
                  disabled={readOnly}
                />
                <span className={`checkmark ${readOnly ? "readOnly" : ""}`} />
                Enable School Names
              </label>
            </div>

            <div>
              <label className="custom-check checkbox-label">
                <input
                  type="checkbox"
                  name="singleRoom"
                  checked={singleRoom}
                  disabled={readOnly}
                  onChange={() => {
                    setSingleRoom((prev) => !prev);
                    setIsDirty(true);
                  }}
                />
                <span className={`checkmark ${readOnly ? "readOnly" : ""}`} />
                Enable Single Room Mode
              </label>
            </div>

            <div>
              <label className="custom-check checkbox-label">
                <input
                  type="checkbox"
                  name="showDifficultyIndicators"
                  checked={showDifficultyIndicators}
                  disabled={readOnly}
                  onChange={() => {
                    setShowDifficultyIndicators((prev) => !prev);
                    setIsDirty(true);
                  }}
                />
                <span className={`checkmark ${readOnly ? "readOnly" : ""}`} />
                Show Room Difficulty Indicators
              </label>
            </div>

          {/* Branding -------------------------------------------------- */}
          <div className="form-group">
            <span className="label-text" style={{ display: "block", marginBottom: 8 }}>
              Event branding (displays on the Leaderboard)
            </span>

            <div className="radio-row">
              <label className="custom-radio radio-label">
                <input
                  type="radio"
                  name="branding"
                  value="gemba"
                  checked={branding === "gemba"}
                  disabled={readOnly}
                  onChange={() => { setBranding("gemba"); setIsDirty(true); }}
                />
                <span className={`radio-mark ${readOnly ? "readOnly" : ""}`} />
                Gemba
              </label>

              <label className="custom-radio radio-label">
                <input
                  type="radio"
                  name="branding"
                  value="actica"
                  checked={branding === "actica"}
                  disabled={readOnly}
                  onChange={() => { setBranding("actica"); setIsDirty(true); }}
                />
                <span className={`radio-mark ${readOnly ? "readOnly" : ""}`} />
                Actica
              </label>

              <label className="custom-radio radio-label">
                <input
                  type="radio"
                  name="branding"
                  value="ntegra"
                  checked={branding === "ntegra"}
                  disabled={readOnly}
                  onChange={() => { setBranding("ntegra"); setIsDirty(true); }}
                />
                <span className={`radio-mark ${readOnly ? "readOnly" : ""}`} />
                Ntegra
              </label>
            </div>
          </div>

          </div>
        </div>

        {/* Rooms panel ------------------------------------------------ */}
        <div className="settings-grid__right">
          <div className="rooms-panel">
            {hasLoadedRoomStates ? (
              <p className="info">
                {hasProfile ? (
                  <>
                    Drag rooms between the panels — or use the + and &times; buttons — to choose
                    which Escape Rooms to play in this Event. <br />
                    Drag within Selected Rooms to set the play order.
                  </>
                ) : (
                  "Select or create a profile to see the available Escape Rooms."
                )}
              </p>
            ) : (
              <p className="info">Loading...</p>
            )}

            {hasLoadedRoomStates && (
              <RoomListPanels
                rooms={updatedRoomStates ?? {}}
                readOnly={readOnly}
                onChange={handleRoomsChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* Action rail ------------------------------------------------- */}
      <div className="button-rail-center" style={{ marginTop: 24, width: "100%" }}>
        <button
          className="btn-blue"
          onClick={saveSettings}
          disabled={!isFormValid || isSaveDisabled}
          style={{ marginRight: "8px" }}
        >
          Save
        </button>
        <button className="btn-blue" onClick={handleClose}>
          Close
        </button>
      </div>

      {feedbackMessage && <p className="feedback">{feedbackMessage}</p>}
    </div>
  );
};

export default Settings;
